// src/pages/DashboardPage/index.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  LinearProgress,
  useTheme,
  Chip,
  Tooltip,
  Paper,
  Button,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import {
  People as PeopleIcon,
  Subscriptions as SubsIcon,
  School as ReadersIcon,
  Timeline as GrowthIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { dashboardService } from '../../services/dashboard-service';
import { toast } from 'react-toastify';
import UserManagementTab from './components/UserManagementTab';
import TenantManagementTab from './components/TenantManagementTab';
import ReaderProfileManagementTab from './components/ReaderProfileManagementTab';
import CampaignBuilderTab from './components/CampaignBuilderTab';
import MonetizationDashboardTab from './components/MonetizationDashboardTab';

// High-fidelity fallback/mock data matching actual DTO output
const mockDashboardData = {
  userStats: {
    totalUsers: 1482,
    statusDistribution: {
      "ACTIVE": 1390,
      "SUSPENDED": 42,
      "LOCKED": 18,
      "DORMANT": 32
    }
  },
  subscriptionStats: {
    typeDistribution: {
      "PAID": 1150,
      "TRIAL": 280,
      "UNLIMITED": 52
    },
    statusDistribution: {
      "ACTIVE": 1420,
      "GRACE_PERIOD": 48,
      "EXPIRED": 14
    },
    quotaUtilization: {
      currentCount: 3480,
      maxCount: 5000,
      utilizationRatio: 0.696
    }
  },
  readerStats: {
    totalProfiles: 4230,
    attachedProfiles: 3480,
    gradeLevelConfidenceMatrix: [
      // Kindergarten
      { gradeLevel: "KINDERGARTEN", readingConfidence: "VERY_LOW", count: 42 },
      { gradeLevel: "KINDERGARTEN", readingConfidence: "LOW", count: 85 },
      { gradeLevel: "KINDERGARTEN", readingConfidence: "MEDIUM", count: 180 },
      { gradeLevel: "KINDERGARTEN", readingConfidence: "HIGH", count: 120 },
      { gradeLevel: "KINDERGARTEN", readingConfidence: "VERY_HIGH", count: 32 },
      // Grade 1
      { gradeLevel: "ONE", readingConfidence: "VERY_LOW", count: 68 },
      { gradeLevel: "ONE", readingConfidence: "LOW", count: 110 },
      { gradeLevel: "ONE", readingConfidence: "MEDIUM", count: 260 },
      { gradeLevel: "ONE", readingConfidence: "HIGH", count: 190 },
      { gradeLevel: "ONE", readingConfidence: "VERY_HIGH", count: 85 },
      // Grade 2
      { gradeLevel: "TWO", readingConfidence: "VERY_LOW", count: 52 },
      { gradeLevel: "TWO", readingConfidence: "LOW", count: 95 },
      { gradeLevel: "TWO", readingConfidence: "MEDIUM", count: 280 },
      { gradeLevel: "TWO", readingConfidence: "HIGH", count: 240 },
      { gradeLevel: "TWO", readingConfidence: "VERY_HIGH", count: 140 },
      // Grade 3
      { gradeLevel: "THREE", readingConfidence: "VERY_LOW", count: 30 },
      { gradeLevel: "THREE", readingConfidence: "LOW", count: 72 },
      { gradeLevel: "THREE", readingConfidence: "MEDIUM", count: 210 },
      { gradeLevel: "THREE", readingConfidence: "HIGH", count: 310 },
      { gradeLevel: "THREE", readingConfidence: "VERY_HIGH", count: 210 },
      // Grade 4
      { gradeLevel: "FOUR", readingConfidence: "VERY_LOW", count: 25 },
      { gradeLevel: "FOUR", readingConfidence: "LOW", count: 55 },
      { gradeLevel: "FOUR", readingConfidence: "MEDIUM", count: 170 },
      { gradeLevel: "FOUR", readingConfidence: "HIGH", count: 280 },
      { gradeLevel: "FOUR", readingConfidence: "VERY_HIGH", count: 240 },
      // Grade 5
      { gradeLevel: "FIVE", readingConfidence: "VERY_LOW", count: 18 },
      { gradeLevel: "FIVE", readingConfidence: "LOW", count: 42 },
      { gradeLevel: "FIVE", readingConfidence: "MEDIUM", count: 140 },
      { gradeLevel: "FIVE", readingConfidence: "HIGH", count: 260 },
      { gradeLevel: "FIVE", readingConfidence: "VERY_HIGH", count: 280 },
      // Grade 6
      { gradeLevel: "SIX", readingConfidence: "VERY_LOW", count: 15 },
      { gradeLevel: "SIX", readingConfidence: "LOW", count: 30 },
      { gradeLevel: "SIX", readingConfidence: "MEDIUM", count: 110 },
      { gradeLevel: "SIX", readingConfidence: "HIGH", count: 210 },
      { gradeLevel: "SIX", readingConfidence: "VERY_HIGH", count: 290 },
      // Grade 7
      { gradeLevel: "SEVEN", readingConfidence: "VERY_LOW", count: 10 },
      { gradeLevel: "SEVEN", readingConfidence: "LOW", count: 22 },
      { gradeLevel: "SEVEN", readingConfidence: "MEDIUM", count: 90 },
      { gradeLevel: "SEVEN", readingConfidence: "HIGH", count: 190 },
      { gradeLevel: "SEVEN", readingConfidence: "VERY_HIGH", count: 260 },
      // Grade 8
      { gradeLevel: "EIGHT", readingConfidence: "VERY_LOW", count: 8 },
      { gradeLevel: "EIGHT", readingConfidence: "LOW", count: 18 },
      { gradeLevel: "EIGHT", readingConfidence: "MEDIUM", count: 75 },
      { gradeLevel: "EIGHT", readingConfidence: "HIGH", count: 170 },
      { gradeLevel: "EIGHT", readingConfidence: "VERY_HIGH", count: 240 },
      // Grade 9
      { gradeLevel: "NINE", readingConfidence: "VERY_LOW", count: 5 },
      { gradeLevel: "NINE", readingConfidence: "LOW", count: 12 },
      { gradeLevel: "NINE", readingConfidence: "MEDIUM", count: 55 },
      { gradeLevel: "NINE", readingConfidence: "HIGH", count: 140 },
      { gradeLevel: "NINE", readingConfidence: "VERY_HIGH", count: 210 },
      // Grade 10
      { gradeLevel: "TEN", readingConfidence: "VERY_LOW", count: 4 },
      { gradeLevel: "TEN", readingConfidence: "LOW", count: 8 },
      { gradeLevel: "TEN", readingConfidence: "MEDIUM", count: 45 },
      { gradeLevel: "TEN", readingConfidence: "HIGH", count: 120 },
      { gradeLevel: "TEN", readingConfidence: "VERY_HIGH", count: 190 },
      // Grade 11
      { gradeLevel: "ELEVEN", readingConfidence: "VERY_LOW", count: 2 },
      { gradeLevel: "ELEVEN", readingConfidence: "LOW", count: 5 },
      { gradeLevel: "ELEVEN", readingConfidence: "MEDIUM", count: 35 },
      { gradeLevel: "ELEVEN", readingConfidence: "HIGH", count: 110 },
      { gradeLevel: "ELEVEN", readingConfidence: "VERY_HIGH", count: 180 },
      // Grade 12
      { gradeLevel: "TWELVE", readingConfidence: "VERY_LOW", count: 1 },
      { gradeLevel: "TWELVE", readingConfidence: "LOW", count: 3 },
      { gradeLevel: "TWELVE", readingConfidence: "MEDIUM", count: 30 },
      { gradeLevel: "TWELVE", readingConfidence: "HIGH", count: 95 },
      { gradeLevel: "TWELVE", readingConfidence: "VERY_HIGH", count: 165 },
      // Adult
      { gradeLevel: "ADULT", readingConfidence: "VERY_LOW", count: 3 },
      { gradeLevel: "ADULT", readingConfidence: "LOW", count: 6 },
      { gradeLevel: "ADULT", readingConfidence: "MEDIUM", count: 25 },
      { gradeLevel: "ADULT", readingConfidence: "HIGH", count: 80 },
      { gradeLevel: "ADULT", readingConfidence: "VERY_HIGH", count: 130 }
    ]
  }
};

const DashboardPage = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isMocked, setIsMocked] = useState(false);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const res = await dashboardService.getPlatformIdentityOverview();
      setData(res);
      setIsMocked(false);
    } catch (err) {
      console.warn("Using high-fidelity local mock data for Platform Identity Overview:", err.message);
      // Automatically fallback to mock data to ensure dashboard functions flawlessly
      setData(mockDashboardData);
      setIsMocked(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading || !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress size={50} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  // Pre-calculate heatmap properties representing standard platform demographics
  const gradeLevels = [
    "Kindergarten",
    "Grade 1",
    "Grade 2",
    "Grade 3",
    "Grade 4",
    "Grade 5",
    "Grade 6",
    "Grade 7",
    "Grade 8",
    "Grade 9",
    "Grade 10",
    "Grade 11",
    "Grade 12",
    "Adult"
  ];

  const confidenceLevels = ["Very Low", "Low", "Medium", "High", "Very High"];

  const gradeMap = {
    'KINDERGARTEN': 'kindergarten',
    'ONE': 'grade 1',
    'TWO': 'grade 2',
    'THREE': 'grade 3',
    'FOUR': 'grade 4',
    'FIVE': 'grade 5',
    'SIX': 'grade 6',
    'SEVEN': 'grade 7',
    'EIGHT': 'grade 8',
    'NINE': 'grade 9',
    'TEN': 'grade 10',
    'ELEVEN': 'grade 11',
    'TWELVE': 'grade 12',
    'ADULT': 'adult'
  };

  const confidenceMap = {
    'VERY_LOW': 'very low',
    'LOW': 'low',
    'MEDIUM': 'medium',
    'HIGH': 'high',
    'VERY_HIGH': 'very high'
  };

  // Helper to normalize grade levels
  const normalizeGrade = (g) => {
    if (!g) return '';
    const clean = g.toString().toUpperCase().trim();
    if (gradeMap[clean]) return gradeMap[clean];
    const cleanLower = clean.toLowerCase();
    if (cleanLower.startsWith('grade ')) return cleanLower;
    const parsed = parseInt(clean, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 12) return `grade ${parsed}`;
    return cleanLower;
  };

  // Helper to normalize confidence levels
  const normalizeConfidence = (c) => {
    if (!c) return '';
    const clean = c.toString().toUpperCase().trim();
    if (confidenceMap[clean]) return confidenceMap[clean];
    return clean.toLowerCase();
  };

  // Helper to query matrix counts
  const getMatrixCount = (grade, confidence) => {
    const targetGrade = normalizeGrade(grade);
    const targetConfidence = normalizeConfidence(confidence);
    
    return data.readerStats.gradeLevelConfidenceMatrix.reduce((sum, row) => {
      if (normalizeGrade(row.gradeLevel) === targetGrade && 
          normalizeConfidence(row.readingConfidence) === targetConfidence) {
        return sum + Number(row.count || 0);
      }
      return sum;
    }, 0);
  };

  // Calculate dynamic max count for dynamic heat color scaling
  const maxCount = Math.max(
    1,
    ...data.readerStats.gradeLevelConfidenceMatrix.map(row => Number(row.count || 0))
  );

  // Helper to determine cell background intensity in M3 theme
  const getCellColor = (count) => {
    if (count === 0) return theme.palette.surfaceContainerLow;
    const ratio = Math.min(count / maxCount, 1.0);
    return theme.palette.mode === 'dark'
      ? `rgba(181, 235, 185, ${0.1 + ratio * 0.8})` // Teal/Mint green scale
      : `rgba(0, 65, 73, ${0.05 + ratio * 0.75})`;  // Deep teal scale
  };

  const getCellTextColor = (count) => {
    if (count === 0) return theme.palette.text.secondary;
    const ratio = count / maxCount;
    if (theme.palette.mode === 'dark') {
      return ratio > 0.5 ? '#031618' : '#ffffff';
    } else {
      return ratio > 0.5 ? '#ffffff' : '#051417';
    }
  };

  return (
    <Box sx={{ animation: 'fadeIn 0.5s ease-out' }}>
      
      {/* Header Panel */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h2" sx={{ color: theme.palette.text.primary, mb: 1 }}>
            Platform Admin Dashboard
          </Typography>
          <Typography variant="subtitle1" sx={{ color: theme.palette.text.secondary }}>
            Real-time analytics, user directory, billing quotas, and learning cohorts
          </Typography>
        </Box>
        {activeTab === 0 && (
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchDashboardData}
            sx={{
              borderRadius: '9999px',
              borderColor: theme.palette.outline,
              textTransform: 'none',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.surfaceContainerHigh
              }
            }}
          >
            Refresh Data
          </Button>
        )}
      </Box>

      {/* Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, val) => setActiveTab(val)}
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
              borderTopLeftRadius: 3,
              borderTopRightRadius: 3
            }
          }}
        >
          <Tab label="Overview" sx={{ textTransform: 'none', fontWeight: 700, fontSize: '15px' }} />
          <Tab label="User Directory" sx={{ textTransform: 'none', fontWeight: 700, fontSize: '15px' }} />
          <Tab label="Tenant Directory" sx={{ textTransform: 'none', fontWeight: 700, fontSize: '15px' }} />
          <Tab label="Reader Profiles" sx={{ textTransform: 'none', fontWeight: 700, fontSize: '15px' }} />
          <Tab label="Devices & Push" sx={{ textTransform: 'none', fontWeight: 700, fontSize: '15px' }} />
          <Tab label="Monetization" sx={{ textTransform: 'none', fontWeight: 700, fontSize: '15px' }} />
        </Tabs>
      </Box>

      {activeTab === 0 ? (
        <Grid container spacing={3}>
        
        {/* KPI Cards Row */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: '16px', 
                backgroundColor: theme.palette.primaryContainer,
                color: theme.palette.onPrimaryContainer
              }}>
                <PeopleIcon />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                  Total Platform Users
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {data.userStats.totalUsers.toLocaleString()}{isMocked && <span style={{ fontSize: '14px', color: theme.palette.error.main, marginLeft: '8px' }}>(Mocked)</span>}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: '16px', 
                backgroundColor: theme.palette.secondaryContainer,
                color: theme.palette.onSecondaryContainer
              }}>
                <SubsIcon />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                  Active Tenant Accounts
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {Object.values(data.subscriptionStats.statusDistribution).reduce((a, b) => a + b, 0).toLocaleString()}{isMocked && <span style={{ fontSize: '14px', color: theme.palette.error.main, marginLeft: '8px' }}>(Mocked)</span>}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: '16px', 
                backgroundColor: theme.palette.primaryContainer,
                color: theme.palette.onPrimaryContainer
              }}>
                <ReadersIcon />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                  Active Reader Profiles
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {data.readerStats.totalProfiles.toLocaleString()}{isMocked && <span style={{ fontSize: '14px', color: theme.palette.error.main, marginLeft: '8px' }}>(Mocked)</span>}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: '16px', 
                backgroundColor: theme.palette.secondaryContainer,
                color: theme.palette.onSecondaryContainer
              }}>
                <GrowthIcon />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                  Attached Readers
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {data.readerStats.attachedProfiles.toLocaleString()}{isMocked && <span style={{ fontSize: '14px', color: theme.palette.error.main, marginLeft: '8px' }}>(Mocked)</span>}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Detailed Stats Cards */}
        <Grid item xs={12} md={5}>
          <Grid container spacing={3}>
            
            {/* User Security and Status Breakdown */}
            <Grid item xs={12}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, textTransform: 'uppercase' }}>
                    User Lifecycle & Security Status
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {Object.entries(data.userStats.statusDistribution).map(([status, count]) => {
                      const total = data.userStats.totalUsers;
                      const percentage = total > 0 ? (count / total) * 100 : 0;
                      
                      // Status colors
                      const colorMap = {
                        "ACTIVE": theme.palette.success.main,
                        "SUSPENDED": theme.palette.warning.main,
                        "LOCKED": theme.palette.error.main,
                        "DORMANT": theme.palette.text.secondary
                      };
                      const statusColor = colorMap[status] || theme.palette.primary.main;

                      return (
                        <Box key={status}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: statusColor }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                                {status.toLowerCase()}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {count.toLocaleString()} ({percentage.toFixed(1)}%)
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{
                              height: 8,
                              borderRadius: '4px',
                              backgroundColor: theme.palette.surfaceContainerHighest,
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: statusColor,
                                borderRadius: '4px'
                              }
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Quota Utilization & Subscription Stats */}
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, textTransform: 'uppercase' }}>
                    Global Profile Quota Allocation
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 4 }}>
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                      <CircularProgress
                        variant="determinate"
                        value={data.subscriptionStats.quotaUtilization.utilizationRatio * 100}
                        size={100}
                        thickness={6}
                        sx={{
                          color: theme.palette.primary.main,
                          backgroundColor: theme.palette.surfaceContainerHighest,
                          borderRadius: '50%'
                        }}
                      />
                      <Box sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                          {Math.round(data.subscriptionStats.quotaUtilization.utilizationRatio * 100)}%
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Quota Utilization
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                        Students Profiles Created vs Total Allowed Pool
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 3 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            CREATED
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {data.subscriptionStats.quotaUtilization.currentCount.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ borderLeft: `1px solid ${theme.palette.divider}`, pl: 3 }}>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            POOL LIMIT
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {data.subscriptionStats.quotaUtilization.maxCount.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Subscription Types and Cohorts */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: 'uppercase', color: theme.palette.text.secondary }}>
                    Account Billing Tiers
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(data.subscriptionStats.typeDistribution).map(([type, count]) => (
                      <Grid item xs={4} key={type}>
                        <Paper sx={{ 
                          p: 1.5, 
                          textAlign: 'center', 
                          borderRadius: '12px',
                          backgroundColor: theme.palette.surfaceContainerLow
                        }}>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                            {type}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                            {count.toLocaleString()}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        </Grid>

        {/* Interactive Grade-Confidence Heatmap Matrix */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                  Reader Profile Cohort Matrix
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                  Demographic mapping of Grade Level vs Reading Confidence Level
                </Typography>
              </Box>

              {/* Heatmap Grid Wrapper */}
              <Box sx={{ 
                flexGrow: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center', 
                mt: 2, 
                overflowX: 'auto' 
              }}>
                <Box sx={{ minWidth: 500 }}>
                  
                  {/* Heatmap Header */}
                  <Grid container sx={{ mb: 1, textAlign: 'center', fontWeight: 600 }}>
                    <Grid item xs={2} sx={{ textAlign: 'left', pl: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        GRADE LEVEL
                      </Typography>
                    </Grid>
                    {confidenceLevels.map((lvl) => (
                      <Grid item xs={2} key={lvl}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.text.secondary }}>
                          {lvl}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Heatmap Rows */}
                  {gradeLevels.map((grade) => (
                    <Grid container key={grade} sx={{ mb: 1, alignItems: 'center', minHeight: 48 }}>
                      
                      {/* Row Header (Grade level) */}
                      <Grid item xs={2}>
                        <Typography variant="body2" sx={{ fontWeight: 700, pl: 1 }}>
                          {grade}
                        </Typography>
                      </Grid>

                      {/* Cells */}
                      {confidenceLevels.map((confidence) => {
                        const count = getMatrixCount(grade, confidence);
                        const isSelected = selectedCohort?.grade === grade && selectedCohort?.confidence === confidence;
                        return (
                          <Grid item xs={2} key={confidence} sx={{ p: 0.5 }}>
                            <Tooltip title={`${grade} - ${confidence}: ${count.toLocaleString()} students`}>
                              <Box
                                onClick={() => setSelectedCohort({ grade, confidence, count })}
                                sx={{
                                  height: 40,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  backgroundColor: getCellColor(count),
                                  color: getCellTextColor(count),
                                  border: isSelected 
                                    ? `2.5px solid ${theme.palette.primary.main}` 
                                    : `1px solid ${theme.palette.outlineVariant}`,
                                  transition: 'all 0.25s ease',
                                  transform: isSelected ? 'scale(1.05)' : 'none',
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    zIndex: 10
                                  }
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  {count.toLocaleString()}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </Grid>
                        );
                      })}

                    </Grid>
                  ))}

                </Box>
              </Box>

              {/* Selected Cohort Metrics Drawer/Footer */}
              <Box sx={{ mt: 3, p: 2, borderRadius: '16px', backgroundColor: theme.palette.surfaceContainerLow }}>
                {selectedCohort ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            Selected: {selectedCohort.grade} - {selectedCohort.confidence}
                          </Typography>
                          <Chip size="small" label="Target Cohort" color="primary" />
                        </Box>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                          Totaling {selectedCohort.count.toLocaleString()} reader profiles across the platform
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          borderRadius: '9999px',
                          px: 3
                        }}
                        onClick={() => toast.success(`Viewing cohort details for ${selectedCohort.grade}`)}
                      >
                        Explore Students
                      </Button>
                    </Box>
                    <Divider sx={{ borderStyle: 'dashed' }} />
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                        PEDAGOGICAL INSIGHT & RECOMMENDATION
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                        {selectedCohort.confidence.toLowerCase() === 'very low' && "⚠️ Critical Intervention Required: Prioritize decodable stories, heavy sound-symbol phonics, and intensive teacher-guided sessions."}
                        {selectedCohort.confidence.toLowerCase() === 'low' && "⚠️ Phonics Support Needed: Focus on letter-sound blends, targeted word-family practice, and small-group guided reading."}
                        {selectedCohort.confidence.toLowerCase() === 'medium' && "📈 Developing Fluency: Student has foundational decoding. Introduce sight-word speed drills, shared reading, and guided comprehension checks."}
                        {selectedCohort.confidence.toLowerCase() === 'high' && "🌟 Steady Progress: Direct student toward independent reading practice, vocabulary extensions, and short quizzes."}
                        {selectedCohort.confidence.toLowerCase() === 'very high' && "👑 Advanced Reading: High fluency and confidence. Open unrestricted library navigation and challenge with long-form texts and analytical activities."}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'center', py: 1 }}>
                    💡 Click on any matrix cell above to explore cohort insights and detail profiles.
                  </Typography>
                )}
              </Box>

            </CardContent>
          </Card>
        </Grid>

      </Grid>
      ) : activeTab === 1 ? (
        <UserManagementTab />
      ) : activeTab === 2 ? (
        <TenantManagementTab />
      ) : activeTab === 3 ? (
        <ReaderProfileManagementTab />
      ) : activeTab === 4 ? (
        <CampaignBuilderTab />
      ) : (
        <MonetizationDashboardTab />
      )}
    </Box>
  );
};

export default DashboardPage;
